/**
 * TODO CAN-182: refactor out the duplicate code between this and `generateNominalCDF.ts`
 *               and `useGenerateDerivedData`; however, this will require a bigger refactor of the rolodex cache
 *               (if we move `useGenerateDerivedData` over to fetch the data in the callback as its done here [which is needed
 *               here so we don't keep all the thickness data arrays in memory], once we put the resulting data into the Rolodex-Cache
 *               that will re-initialize the cache, causing the fetcher to re-initialize, causing the callback to re-initialize, causing
 *               the `createCustomCdfs` function to create a new CDF and insert it into the cache, restarting the cycle).
 * */

import { componentHasDefinedNominal } from 'features/assetModeling'
import { createBinnedDataBufferFromData } from 'features/dataLayers/geckoBinnedDataBuffer'
import LossByPercentageWorker from 'features/dataLayers/generation/coreLayers/wastage/calculateLossByPercentageData.worker?worker'
import { flatten2DValues } from 'features/dataLayers/generation/flatten2DValues'
import { useBinnedDataBufferCache } from 'features/dataLayers/load/useBinnedDataBufferCache'
import { useGetCdf2dDataFetcher } from 'features/dataLayers/load/useCdfRawValues2DArray'
import { getInspectionDate } from 'features/fde/inspectionDateHelpers'
import isNil from 'lodash/isNil'
import memoize from 'lodash/memoize'
import { useCallback, useMemo } from 'react'
import {
  CacheableRolodexEntryJson,
  ForecastComponentConfig,
  GeckoAssetModel,
  GeckoComponentDataFrame,
  GeckoEntryBase,
  GeckoInspection,
  GeckoMeasurementUnit,
  GeckoView,
} from 'types'
import { DATE_FORMAT } from 'utils/fde/constants'

import { LossByPercentageWorkerProps } from './calculateLossByPercentageData.worker'

export function useGenerateLossByPercentageCDFs(
  assetModel: GeckoAssetModel | undefined,
  thicknessCDFs: ReadonlyArray<GeckoComponentDataFrame> | undefined,
  inspections: ReadonlyArray<GeckoInspection>,
  componentId: string | undefined,
  forecastComponentConfig: GeckoEntryBase<ForecastComponentConfig> | undefined
): ReadonlyArray<GeckoComponentDataFrame> {
  const cdf2dDataFetcher = useGetCdf2dDataFetcher()

  const calculateLossByPercentageInWorker = useCallback(
    (props: {
      relevantThicknessCDF: GeckoComponentDataFrame
      relevantInspection: GeckoInspection
    }) =>
      Promise.resolve(
        new Promise<{
          relevantInspection: GeckoInspection
          relevantThicknessCDF: GeckoComponentDataFrame
          values: Readonly<Float32Array>
        }>((resolve, reject) => {
          const { relevantThicknessCDF, relevantInspection } = props
          const inspectionDate = getInspectionDate({
            inspectionSlug: relevantInspection.id,
            inspection: relevantInspection,
          })

          const inspectionDateString = inspectionDate
            ? inspectionDate.format(DATE_FORMAT)
            : undefined

          if (
            isNil(assetModel) ||
            isNil(componentId) ||
            !inspectionDateString
          ) {
            reject('Invalid input data, something needed is not defined')
            return
          }

          cdf2dDataFetcher(relevantThicknessCDF).then(
            (thicknessValues2d) => {
              try {
                if (isNil(thicknessValues2d)) {
                  reject('No thickness values returned from fetch')
                  return
                }

                const workerProps: LossByPercentageWorkerProps = {
                  componentId,
                  assetModelJson: assetModel.raw,
                  thicknessValues: thicknessValues2d,
                  thicknessUnit:
                    relevantThicknessCDF.data.params.measurement_unit.id,
                  binHeightJson:
                    relevantThicknessCDF.data.binning.bin_size.height.toJSON(),
                  binWidthJson:
                    relevantThicknessCDF.data.binning.bin_size.width.toJSON(),
                  yGeometryStartJson:
                    relevantThicknessCDF.data.geometry.start.y.toJSON(),
                  xGeometryStartJson:
                    relevantThicknessCDF.data.geometry.start.x.toJSON(),
                  forecastComponentConfig: forecastComponentConfig?.data,
                  inspectionDateString,
                }

                const lossByPercentageWorker = new LossByPercentageWorker()

                lossByPercentageWorker.onmessage = async (message) => {
                  lossByPercentageWorker.terminate()
                  resolve({
                    relevantInspection,
                    relevantThicknessCDF,
                    values: flatten2DValues(message.data as number[][]),
                  })
                }

                lossByPercentageWorker.onerror = async (error) => {
                  console.error(error)
                  lossByPercentageWorker.terminate()
                  reject(
                    `Couldn't calculate loss-by-percentage CDF, ${error.message}`
                  )
                }

                lossByPercentageWorker.onmessageerror = async (error) => {
                  console.error(error)
                  lossByPercentageWorker.terminate()
                  reject("Couldn't receive loss-by-percentage CDF data")
                }

                lossByPercentageWorker.postMessage(workerProps)
              } catch (error) {
                reject(error)
              }
            },
            (error: unknown) => {
              reject(
                error instanceof Error
                  ? error
                  : new Error(
                      `Couldn't fetch thickness values for loss-by-percentage CDF: ${String(
                        error
                      )}`
                    )
              )
            }
          )
        })
      ),

    [assetModel, cdf2dDataFetcher, componentId, forecastComponentConfig]
  )

  // ------------------------------------------------------------
  // Function to generate a loss by percentage CDF and insert its data into the `cdfDataFetchCache`
  const { putBinnedDataBufferInCache } = useBinnedDataBufferCache()

  const assembleLossByPercentageCDF = useCallback(
    (
      baseThicknessCdf: GeckoComponentDataFrame,
      relevantInspection: GeckoInspection,
      assetModel: GeckoAssetModel
    ) => {
      const thicknessCdfRaw = baseThicknessCdf.raw

      // Remove the stats and texture from the data, as they don't carry over
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const { stats, texture, ...usableCdfData } = thicknessCdfRaw.data

      const thisId = `wastage-${baseThicknessCdf.id}`
      const thisGeneration =
        baseThicknessCdf.generation *
        assetModel.generation *
        (forecastComponentConfig?.generation ?? 1)
      const cdfEntry: CacheableRolodexEntryJson = {
        isFromCache: true,
        ...thicknessCdfRaw,
        id: thisId,
        generation: thisGeneration,
        data: {
          ...usableCdfData,
          is_default: false,
          params: {
            ...usableCdfData.params!, // we know this is a cdf so we can assert this
            view: GeckoView.DifferenceFromNominal.id,
            measurement_unit: GeckoMeasurementUnit.Percent.id,
          },
        },
      }

      const cdfObject = GeckoComponentDataFrame.CreateFromJSON(cdfEntry)

      const memoizedGetterFn = memoize(async () =>
        createBinnedDataBufferFromData(
          (
            await calculateLossByPercentageInWorker({
              relevantThicknessCDF: baseThicknessCdf,
              relevantInspection,
            })
          ).values,
          cdfObject
        )
      )

      putBinnedDataBufferInCache({
        id: thisId,
        generation: thisGeneration,
        data: memoizedGetterFn,
      })

      return cdfObject
    },
    [
      calculateLossByPercentageInWorker,
      putBinnedDataBufferInCache,
      forecastComponentConfig?.generation,
    ]
  )

  // ------------------------------------------------------------
  // assemble the loss by percentage CDFs for each thickness CDF
  return useMemo(() => {
    const componentModel = assetModel?.data.components.get(componentId ?? '')

    if (
      !componentId ||
      !assetModel ||
      !thicknessCDFs?.length ||
      !inspections.length ||
      !componentModel ||
      !componentHasDefinedNominal(componentModel)
    ) {
      return []
    }

    return thicknessCDFs
      .map((relevantThicknessCdf) => {
        const inspectionToUse = inspections.find(
          (insp) => insp.id === relevantThicknessCdf.tags.inspection
        )

        if (!inspectionToUse) {
          console.warn(
            `No inspection match found for thickness CDF: ${relevantThicknessCdf.id}`
          )
          return undefined
        }

        return assembleLossByPercentageCDF(
          relevantThicknessCdf,
          inspectionToUse,
          assetModel
        )
      })
      .filter((cdf): cdf is GeckoComponentDataFrame => !!cdf)
  }, [
    assembleLossByPercentageCDF,
    assetModel,
    componentId,
    inspections,
    thicknessCDFs,
  ])
}
